export interface GoogleMapsLibrary {
  Map: new (element: HTMLElement, options?: Record<string, unknown>) => unknown;
  Marker: new (
    options?: Record<string, unknown>
  ) => {
    addListener: (event: string, handler: () => void) => void;
    setMap: (map: unknown) => void;
  };
  Polyline: new (
    options?: Record<string, unknown>
  ) => {
    setMap: (map: unknown) => void;
  };
  InfoWindow: new () => {
    setContent: (content: string) => void;
    open: (map: unknown, marker?: unknown) => void;
  };
  LatLngBounds: new () => {
    extend: (latLng: { lat: number; lng: number }) => void;
  };
  SymbolPath: {
    CIRCLE: unknown;
    FORWARD_CLOSED_ARROW: unknown;
  };
  places?: {
    Autocomplete: new (
      input: HTMLInputElement,
      options?: Record<string, unknown>
    ) => {
      addListener: (event: string, handler: () => void) => void;
      getPlace: () => {
        formatted_address?: string;
        name?: string;
        place_id?: string;
        address_components?: Array<{
          long_name: string;
          short_name: string;
          types: string[];
        }>;
        geometry?: {
          location?: {
            lat: () => number;
            lng: () => number;
          };
        };
      };
    };
  };
}

declare global {
  interface Window {
    google?: {
      maps?: GoogleMapsLibrary;
    };
    _googleMapsPromise?: Promise<GoogleMapsLibrary>;
  }
}

let loadPromise: Promise<GoogleMapsLibrary> | null = null;

export function loadGoogleMaps(apiKey?: string): Promise<GoogleMapsLibrary> {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window not available'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (loadPromise) return loadPromise;

  const key =
    apiKey ||
    (import.meta as unknown as { env?: { VITE_GOOGLE_MAPS_API_KEY?: string } }).env
      ?.VITE_GOOGLE_MAPS_API_KEY;

  if (!key) {
    return Promise.reject(new Error('VITE_GOOGLE_MAPS_API_KEY is not configured'));
  }

  loadPromise = new Promise<GoogleMapsLibrary>((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      const checkInterval = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(checkInterval);
          resolve(window.google.maps);
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google?.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps script loaded without google.maps object'));
      }
    };

    script.onerror = (err) => {
      loadPromise = null;
      reject(new Error(`Failed to load Google Maps script: ${String(err)}`));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}
